import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import {
  Comment,
  MessageType,
  UserMessage,
  WebsocketConversationMetaData,
} from 'api/conversations';
import { ProjectGlobalConversation } from 'api/project-conversations';
import {
  AccountSocketData,
  Action,
  EventType,
  ObjectType,
  SocketData as ProjectSocketData,
} from 'api/pushes';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useUser } from 'hooks/use-user';
import { useUserWsUpdates } from 'hooks/use-user-ws-updates';
import {
  constructGlobalConversation,
  globalConversationQueryKey,
  GlobalConversationQueryResult,
} from './utils';

type SocketData<T> = ProjectSocketData<T> | AccountSocketData<T>;

const calculateUnreadCount = (
  preData: ProjectGlobalConversation | undefined,
  action: Action,
) => {
  let oldCount = preData?.unreadMessages || 0;

  if (action === Action.Create) {
    oldCount += 1;
  } else if (action === Action.Delete && oldCount) {
    oldCount -= 1;
  }

  return oldCount;
};

const getUpdatedQueryResult = (
  myId: number,
  socketData: SocketData<UserMessage & WebsocketConversationMetaData>,
  queryResult?: GlobalConversationQueryResult,
): GlobalConversationQueryResult | undefined => {
  const getActive = (conversation) =>
    conversation.entityId === socketData.data.commentContext.id;
  const preData =
    queryResult?.conversations?.find(getActive) ??
    constructGlobalConversation(socketData);

  if (!preData) {
    return undefined;
  }

  const isActionByMe = socketData.data.createdBy === myId;
  let totalCount = queryResult?.unread ?? 0;

  if (!isActionByMe) {
    totalCount +=
      calculateUnreadCount(preData, socketData.action) - preData.unreadMessages;
    preData.unreadMessages = calculateUnreadCount(preData, socketData.action);
  }

  const rest =
    queryResult?.conversations?.filter((chat) => !getActive(chat)) ?? [];

  return { conversations: [preData, ...rest], unread: totalCount };
};

export const useRearrangeGlobalConversationInReactQuery = (
  projectId: number,
): void => {
  const {
    user: { userId },
  } = useUser();
  const queryClient = useQueryClient();
  const rearrangeConversation = useCallback(
    (socketData: SocketData<Comment & WebsocketConversationMetaData>) => {
      if (socketData.data.messageType !== MessageType.UserMessage) {
        return;
      }
      const queryKey = globalConversationQueryKey.byProjectId(projectId);
      const queryResult =
        queryClient.getQueryData<GlobalConversationQueryResult>(queryKey);
      const updatedResult = getUpdatedQueryResult(
        userId,
        socketData as SocketData<UserMessage & WebsocketConversationMetaData>,
        queryResult,
      );

      if (!updatedResult) {
        return;
      }

      queryClient.setQueryData<GlobalConversationQueryResult>(
        queryKey,
        updatedResult,
      );
    },
    [projectId, queryClient, userId],
  );

  useProjectWSUpdates<Comment & WebsocketConversationMetaData>(
    projectId,
    rearrangeConversation,
    EventType(Action.Create, ObjectType.ConversationComment),
  );
  useUserWsUpdates<Comment & WebsocketConversationMetaData>(
    rearrangeConversation,
    EventType(Action.Create, ObjectType.ConversationComment),
  );
};
